import React from 'react';
import { translate, useT } from '@/i18n/labels';
import { useAgentStore } from '@/stores/agentStore';
import { useAuthStore } from '@/stores/authStore';
import { formatTime } from '@/utils/formatters';
import { describeTimelineEntry, opensConsultation } from '@/utils/timeline';
import type { TimelineEntry } from '@/types';

/**
 * One chat-timeline line between the messages: a small centred "14:02 · Bot consulted Adam".
 *
 * STAFF-ONLY by construction - it renders only inside the agent console's transcript, from entries the
 * staff routes and the agent hub deliver. It shows no message text and no raw summary: the sentence is built
 * from the entry's type and params in the reader's language, and a person is shown by NAME from the agent
 * directory ("(you)" for the signed-in user), never by id. Everything is rendered as text nodes, so a
 * staff-typed takeover reason cannot inject markup.
 *
 * When `onOpenConsultation` is given, the consultation lines (bot asked / bot consulted / answered the bot)
 * become a button that takes the agent to the Bot consultation tab.
 */
export const TimelineRow: React.FC<{
  entry: TimelineEntry;
  onOpenConsultation?: () => void;
}> = ({ entry, onOpenConsultation }) => {
  const { lang } = useT();
  const directory = useAgentStore((s) => s.directory);
  const me = useAuthStore((s) => s.user);

  const { text, title } = describeTimelineEntry(entry, {
    lang,
    tr: (key, vars) => translate(key, lang, vars),
    nameOf: (id) => directory[id],
    myId: me?.agentId ?? null,
    myName: me?.displayName ?? null,
  });

  const body = (
    <>
      <span className="ac-msg-system-time">{formatTime(entry.occurredAtUtc)}</span>
      <span className="ac-msg-system-sep" aria-hidden="true">
        ·
      </span>
      {/* dir="auto": a Latin name inside a Hebrew sentence (or the reverse) must not flip the sentence. */}
      <span className="ac-msg-system-text" dir="auto">
        {text}
      </span>
    </>
  );

  if (onOpenConsultation && opensConsultation(entry.type)) {
    return (
      <button
        type="button"
        className="ac-msg-system ac-msg-system-link"
        data-testid="timeline-row"
        data-timeline-type={entry.type}
        title={title}
        onClick={onOpenConsultation}
      >
        {body}
      </button>
    );
  }

  return (
    <div className="ac-msg-system" data-testid="timeline-row" data-timeline-type={entry.type} title={title}>
      {body}
    </div>
  );
};
